import axios from "axios";
import { authStorage } from "../auth/authStorage";
import { normalizeApiError } from "./apiError";
import { logger } from "../logger";
import { getConfig } from "../../config";

// Single axios instance, baseURL set lazily in interceptor
const instance = axios.create({
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor — set baseURL and auth token lazily
instance.interceptors.request.use((config) => {
  // getConfig() is safe here — interceptor runs after loadConfig() resolves
  const { apiUrl } = getConfig();
  config.baseURL = apiUrl;

  const token = authStorage.getToken();
  if (token) {
    config.headers = new axios.AxiosHeaders(config.headers);
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  return config;
});

// Response interceptor — normalize errors
instance.interceptors.response.use(
  (response) => response,
  (error) => {
    const normalized = normalizeApiError(error);
    if (normalized.type === "AUTH_EXPIRED") {
      // Only an EXPIRY if a session existed. A 401 on a request that carried no token
      // means "you are not signed in", which is the normal state of a visitor reading a
      // public page — and firing auth-expired for it drags them to /Login from wherever
      // they were. The redirect belongs to a session that ENDED, not to its absence.
      const hadSession = Boolean(error?.config?.headers?.Authorization);
      authStorage.clearSession();
      if (hadSession) {
        window.dispatchEvent(new CustomEvent("ecogynize:auth-expired"));
      }
    } else if (normalized.type === "SERVER_ERROR" || normalized.type === "NETWORK_ERROR") {
      logger.error("API error", {
        type: normalized.type,
        status: normalized.status,
        message: normalized.message,
        url: error?.config?.url,
      });
    }
    return Promise.reject(normalized);
  }
);

/**
 * Typed request helper for data-only calls.
 * @template T
 * @param {import('axios').AxiosRequestConfig} config
 * @returns {Promise<T>}
 */
export const requestData = async (config) => {
  const response = await instance.request(config);  // instance, not apiClient
  return response.data;
};

export default instance;